import * as THREE from 'three';
import {FunctionAllRootsSolver} from './FunctionAllRootsSolver';
import {FunctionRootsSolver} from './FunctionRootsSolver';
import {ImplicitConicTool2D} from './ImplicitConicTool2D';
import {ParametricSplineCurveAdaptor2D} from './ParametricSplineCurveAdaptor2D';

/**
 * Puppy CAD 内核 `IntImpParGen_Intersector` 的二维隐式二次曲线—参数样条等价实现。
 *
 * 职责：按 C1 区间执行原生参数采样和全根搜索，构造完整 transition，处理容差零区间、
 * 周期参数、端点补查、point 去重及 segment 合并。
 */
export class ImplicitParametricCurveIntersector2D {
    /** Puppy CAD 内核 内部参数容差上限与下限。 */
    private static readonly INTERNAL_PARAMETER_TOLERANCE = 1e-10;

    /** Puppy CAD 内核 point/segment 参数相等宏阈值。 */
    private static readonly PARAMETER_EQUAL_TOLERANCE = 1e-8;

    /** Puppy CAD 内核 transition 平行角阈值。 */
    private static readonly ANGULAR_TOLERANCE = 1e-8;

    /** Puppy CAD 内核 一阶导平方退化阈值。 */
    private static readonly FIRST_DERIVATIVE_SQUARED_TOLERANCE = 1e-12;

    /** 默认公开几何容差。 */
    private static readonly DEFAULT_TOLERANCE = 1e-6;

    /** 全根状态机。 */
    private readonly allRootsSolver: FunctionAllRootsSolver;

    /** segment domain 裁剪使用的离散根器。 */
    private readonly rootsSolver: FunctionRootsSolver;

    /**
     * 创建 Puppy CAD 内核 等价隐式—参数曲线求交器。
     * @param allRootsSolver 全根与零区间状态机。
     * @param rootsSolver 离散根器。
     */
    public constructor(
        allRootsSolver: FunctionAllRootsSolver = new FunctionAllRootsSolver(),
        rootsSolver: FunctionRootsSolver = new FunctionRootsSolver(),
    ) {
        this.allRootsSolver = allRootsSolver;
        this.rootsSolver = rootsSolver;
    }

    /**
     * 执行 Puppy CAD 内核 等价求交。
     * @param conicTool 直线、圆或椭圆隐式工具。
     * @param curveAdaptor Bezier/B-Spline 原生参数适配器。
     * @param options `TolConf` 与 `Tol`。
     * @returns 完整 point、segment、done 与错误状态。
     */
    public perform(
        conicTool: ImplicitConicTool2D,
        curveAdaptor: ParametricSplineCurveAdaptor2D,
        options?: ImplicitParametricIntersectionOptions,
    ): ImplicitParametricIntersectionResult2D {
        if (!conicTool.isValid() || !curveAdaptor.isValid()) {
            return {done: false, points: [], segments: [], error: '隐式曲线或参数样条定义无效。'};
        }
        const toleranceConfusion = Math.max(
            options?.confusionTolerance ?? ImplicitParametricCurveIntersector2D.DEFAULT_TOLERANCE,
            ImplicitParametricCurveIntersector2D.INTERNAL_PARAMETER_TOLERANCE,
        );
        const toleranceDistance = Math.max(
            options?.distanceTolerance ?? ImplicitParametricCurveIntersector2D.DEFAULT_TOLERANCE,
            ImplicitParametricCurveIntersector2D.INTERNAL_PARAMETER_TOLERANCE,
        );
        const domainTolerance = Math.max(toleranceConfusion, toleranceDistance);
        const implicitDomain = conicTool.createDomain(domainTolerance);
        const curveDomain = curveAdaptor.createDomain(domainTolerance);
        const intervalBoundaries = curveAdaptor.resolveC1IntervalBoundaries();
        const points: ImplicitParametricPointIntersection2D[] = [];
        const segments: ImplicitParametricSegmentIntersection2D[] = [];

        for (let intervalIndex = 0; intervalIndex < intervalBoundaries.length - 1; intervalIndex += 1) {
            const first = intervalBoundaries[intervalIndex]!;
            const last = intervalBoundaries[intervalIndex + 1]!;
            if (last - first <= ImplicitParametricCurveIntersector2D.INTERNAL_PARAMETER_TOLERANCE) continue;
            let epsilonX = ImplicitParametricCurveIntersector2D.INTERNAL_PARAMETER_TOLERANCE;
            if (last - first < 100 * epsilonX) epsilonX = (last - first) * 0.01;
            const sampleCount = curveAdaptor.resolveSampleCount(first, last);
            const scalarFunction = this.createImplicitParametricFunction(conicTool, curveAdaptor);
            const result = this.allRootsSolver.solve(
                scalarFunction,
                first,
                last,
                sampleCount,
                epsilonX,
                toleranceDistance,
                toleranceConfusion,
            );
            if (!result.done) {
                return {done: false, points: [], segments: [], error: 'Puppy CAD 内核 等价全根状态机未完成。'};
            }
            result.roots.forEach((curveParameter) => {
                const point = this.buildPoint(
                    conicTool,
                    curveAdaptor,
                    implicitDomain,
                    curveDomain,
                    curveParameter,
                );
                if (point) this.insertPoint(points, point);
            });
            result.nullIntervals.forEach((interval) => {
                this.resolveSegmentRanges(
                    conicTool,
                    curveAdaptor,
                    implicitDomain,
                    interval.startParameter,
                    interval.endParameter,
                    epsilonX,
                    toleranceDistance,
                ).forEach(([segmentStart, segmentEnd]) => {
                    const segment = this.buildSegment(
                        conicTool,
                        curveAdaptor,
                        implicitDomain,
                        curveDomain,
                        segmentStart,
                        segmentEnd,
                    );
                    if (segment) this.appendSegment(segments, segment);
                });
            });
        }

        this.appendEndpointIntersections(
            points,
            conicTool,
            curveAdaptor,
            implicitDomain,
            curveDomain,
        );
        this.removePointsInsideSegments(points, segments);

        // 公共结果以参数样条自身的遍历方向为稳定顺序，隐式曲线参数仅描述交点在圆锥曲线上的位置。
        points.sort((left, right) => left.curveParameter - right.curveParameter);
        return {done: true, points, segments, error: null};
    }

    /** 创建 `F(u)=Implicit(Curve(u))` 及 `F'(u)=Gradient·C'(u)`。 */
    private createImplicitParametricFunction(
        conicTool: ImplicitConicTool2D,
        curveAdaptor: ParametricSplineCurveAdaptor2D,
    ): ScalarFunction {
        return {
            value: (parameter) => {
                const point = curveAdaptor.value(parameter);
                const value = conicTool.value(point);
                return Number.isFinite(value) ? value : null;
            },
            derivative: (parameter) => {
                const point = curveAdaptor.value(parameter);
                const value = conicTool.gradient(point).dot(curveAdaptor.derivative(parameter));
                return Number.isFinite(value) ? value : null;
            },
        };
    }

    /** 根据参数样条根构造 Puppy CAD 内核 以隐式曲线点为坐标代表的交点。 */
    private buildPoint(
        conicTool: ImplicitConicTool2D,
        curveAdaptor: ParametricSplineCurveAdaptor2D,
        implicitDomain: ParametricIntersectionDomain2D,
        curveDomain: ParametricIntersectionDomain2D,
        curveParameter: number,
    ): ImplicitParametricPointIntersection2D | null {
        const curvePoint = curveAdaptor.value(curveParameter);
        let implicitParameter = conicTool.parameter(curvePoint);
        implicitParameter = conicTool.normalizeOnDomain(implicitParameter, implicitDomain);
        if (!this.isParameterAccepted(implicitParameter, curvePoint, implicitDomain)) return null;
        const point = conicTool.pointAt(implicitParameter);
        const transitions = this.determineTransitions(
            conicTool,
            curveAdaptor,
            implicitDomain,
            curveDomain,
            implicitParameter,
            curveParameter,
            point,
        );
        return {
            point,
            implicitParameter,
            curveParameter,
            implicitTransition: transitions.implicitTransition,
            curveTransition: transitions.curveTransition,
        };
    }

    /** 构造零区间对应的 Puppy CAD 内核 segment。 */
    private buildSegment(
        conicTool: ImplicitConicTool2D,
        curveAdaptor: ParametricSplineCurveAdaptor2D,
        implicitDomain: ParametricIntersectionDomain2D,
        curveDomain: ParametricIntersectionDomain2D,
        firstCurveParameter: number,
        secondCurveParameter: number,
    ): ImplicitParametricSegmentIntersection2D | null {
        const firstEndpoint = this.buildPoint(
            conicTool,
            curveAdaptor,
            implicitDomain,
            curveDomain,
            firstCurveParameter,
        );
        const secondEndpoint = this.buildPoint(
            conicTool,
            curveAdaptor,
            implicitDomain,
            curveDomain,
            secondCurveParameter,
        );
        if (!firstEndpoint || !secondEndpoint) return null;
        let secondImplicitParameter = secondEndpoint.implicitParameter;
        if (
            implicitDomain.closed
            && implicitDomain.equivalentFirstParameter !== null
            && implicitDomain.equivalentLastParameter !== null
        ) {
            const period = implicitDomain.equivalentLastParameter - implicitDomain.equivalentFirstParameter;
            const sameDirection = conicTool.derivative(firstEndpoint.implicitParameter)
                .dot(curveAdaptor.derivative(firstCurveParameter)) >= 0;
            if (sameDirection && secondImplicitParameter < firstEndpoint.implicitParameter) {
                secondImplicitParameter += period;
            } else if (!sameDirection && secondImplicitParameter > firstEndpoint.implicitParameter) {
                secondImplicitParameter -= period;
            }
            secondEndpoint.implicitParameter = secondImplicitParameter;
        }
        const opposite = conicTool.derivative((firstEndpoint.implicitParameter + secondImplicitParameter) * 0.5)
            .dot(curveAdaptor.derivative((firstCurveParameter + secondCurveParameter) * 0.5)) < 0;
        return {firstEndpoint, secondEndpoint, opposite};
    }

    /**
     * 将容差零区间裁到非周期隐式曲线真实 domain。
     * 对完整圆/椭圆不裁剪；有限直线通过其参数 `0/1` 根切分。
     */
    private resolveSegmentRanges(
        conicTool: ImplicitConicTool2D,
        curveAdaptor: ParametricSplineCurveAdaptor2D,
        domain: ParametricIntersectionDomain2D,
        first: number,
        last: number,
        epsilonX: number,
        epsilonF: number,
    ): Array<[number, number]> {
        if (
            domain.closed
            || !domain.hasFirstPoint
            || !domain.hasLastPoint
            || domain.firstParameter === null
            || domain.lastParameter === null
        ) return [[first, last]];
        const parameterFunction: ScalarFunction = {
            value: (parameter) => conicTool.parameter(curveAdaptor.value(parameter)),
            derivative: (parameter) => {
                const step = Math.max((last - first) * 1e-6, 1e-12);
                const before = Math.max(first, parameter - step);
                const after = Math.min(last, parameter + step);
                if (after <= before) return 0;
                return (
                    conicTool.parameter(curveAdaptor.value(after))
                    - conicTool.parameter(curveAdaptor.value(before))
                ) / (after - before);
            },
        };
        const boundaries = [first, last];
        [domain.firstParameter, domain.lastParameter].forEach((target) => {
            const roots = this.rootsSolver.solve(parameterFunction, first, last, 3, epsilonX, epsilonF, target);
            if (roots.done) boundaries.push(...roots.roots);
        });
        const uniqueBoundaries = boundaries
            .sort((left, right) => left - right)
            .filter((value, index, values) => index === 0 || Math.abs(value - values[index - 1]!) > epsilonX);
        const ranges: Array<[number, number]> = [];
        for (let index = 0; index < uniqueBoundaries.length - 1; index += 1) {
            const rangeStart = uniqueBoundaries[index]!;
            const rangeEnd = uniqueBoundaries[index + 1]!;
            const middleParameter = conicTool.parameter(curveAdaptor.value((rangeStart + rangeEnd) * 0.5));
            if (middleParameter >= domain.firstParameter - epsilonF && middleParameter <= domain.lastParameter + epsilonF) {
                ranges.push([rangeStart, rangeEnd]);
            }
        }
        return ranges;
    }

    /** 构造双方完整 position/transition。 */
    private determineTransitions(
        conicTool: ImplicitConicTool2D,
        curveAdaptor: ParametricSplineCurveAdaptor2D,
        implicitDomain: ParametricIntersectionDomain2D,
        curveDomain: ParametricIntersectionDomain2D,
        implicitParameter: number,
        curveParameter: number,
        point: THREE.Vector2,
    ): {implicitTransition: ImplicitParametricIntersectionTransition2D; curveTransition: ImplicitParametricIntersectionTransition2D} {
        const implicitPosition = this.determinePosition(implicitDomain, implicitParameter, point);
        const curvePosition = this.determinePosition(curveDomain, curveParameter, point);
        let implicitTangent = conicTool.derivative(implicitParameter);
        let curveTangent = curveAdaptor.derivative(curveParameter);
        const implicitSecond = conicTool.secondDerivative(implicitParameter);
        const curveSecond = curveAdaptor.secondDerivative(curveParameter);
        let implicitFirstOrder = true;
        let curveFirstOrder = true;
        if (implicitTangent.lengthSq() <= ImplicitParametricCurveIntersector2D.FIRST_DERIVATIVE_SQUARED_TOLERANCE) {
            implicitTangent = implicitSecond.clone();
            implicitFirstOrder = false;
        }
        if (curveTangent.lengthSq() <= ImplicitParametricCurveIntersector2D.FIRST_DERIVATIVE_SQUARED_TOLERANCE) {
            curveTangent = curveSecond.clone();
            curveFirstOrder = false;
        }
        if (
            implicitTangent.lengthSq() <= ImplicitParametricCurveIntersector2D.FIRST_DERIVATIVE_SQUARED_TOLERANCE
            || curveTangent.lengthSq() <= ImplicitParametricCurveIntersector2D.FIRST_DERIVATIVE_SQUARED_TOLERANCE
        ) {
            return {
                implicitTransition: this.createTransition(implicitPosition, 'undecided', 'unknown', false),
                curveTransition: this.createTransition(curvePosition, 'undecided', 'unknown', false),
            };
        }
        const cross = this.cross(implicitTangent, curveTangent);
        const norm = implicitTangent.length() * curveTangent.length();
        if (Math.abs(cross) <= ImplicitParametricCurveIntersector2D.ANGULAR_TOLERANCE * norm) {
            const opposite = implicitTangent.dot(curveTangent) < 0;
            let implicitSituation: ImplicitParametricIntersectionSituation2D = 'unknown';
            let curveSituation: ImplicitParametricIntersectionSituation2D = 'unknown';
            if (implicitFirstOrder && curveFirstOrder) {
                const normal = new THREE.Vector2(-implicitTangent.y, implicitTangent.x);
                const implicitCurvature = normal.dot(implicitSecond);
                const curveCurvature = normal.dot(curveSecond) * (opposite ? -1 : 1);
                if (Math.abs(implicitCurvature - curveCurvature) > ImplicitParametricCurveIntersector2D.ANGULAR_TOLERANCE) {
                    implicitSituation = implicitCurvature < curveCurvature ? 'inside' : 'outside';
                    curveSituation = implicitSituation === 'inside' ? 'outside' : 'inside';
                }
            }
            return {
                implicitTransition: this.createTransition(implicitPosition, 'touch', implicitSituation, opposite),
                curveTransition: this.createTransition(curvePosition, 'touch', curveSituation, opposite),
            };
        }
        return cross < 0
            ? {
                implicitTransition: this.createTransition(implicitPosition, 'in', 'unknown', false),
                curveTransition: this.createTransition(curvePosition, 'out', 'unknown', false),
            }
            : {
                implicitTransition: this.createTransition(implicitPosition, 'out', 'unknown', false),
                curveTransition: this.createTransition(curvePosition, 'in', 'unknown', false),
            };
    }

    /** 判断世界点和参数在有界 domain 中的位置。 */
    private determinePosition(
        domain: ParametricIntersectionDomain2D,
        parameter: number,
        point: THREE.Vector2,
    ): ImplicitParametricIntersectionPosition2D {
        const atFirst = domain.hasFirstPoint
            && domain.firstPoint !== null
            && point.distanceTo(domain.firstPoint) <= domain.firstTolerance;
        const atLast = domain.hasLastPoint
            && domain.lastPoint !== null
            && point.distanceTo(domain.lastPoint) <= domain.lastTolerance;
        if (atFirst && atLast && domain.firstParameter !== null && domain.lastParameter !== null) {
            return Math.abs(parameter - domain.firstParameter) <= Math.abs(parameter - domain.lastParameter)
                ? 'head'
                : 'end';
        }
        if (atFirst) return 'head';
        if (atLast) return 'end';
        return 'middle';
    }

    /** 显式检查双方四种有限端点组合。 */
    private appendEndpointIntersections(
        points: ImplicitParametricPointIntersection2D[],
        conicTool: ImplicitConicTool2D,
        curveAdaptor: ParametricSplineCurveAdaptor2D,
        implicitDomain: ParametricIntersectionDomain2D,
        curveDomain: ParametricIntersectionDomain2D,
    ): void {
        const implicitEndpoints = [
            implicitDomain.hasFirstPoint ? implicitDomain.firstParameter : null,
            implicitDomain.hasLastPoint ? implicitDomain.lastParameter : null,
        ];
        const curveEndpoints = [curveDomain.firstParameter, curveDomain.lastParameter];
        implicitEndpoints.forEach((implicitParameter) => {
            if (implicitParameter === null) return;
            curveEndpoints.forEach((curveParameter) => {
                if (curveParameter === null) return;
                const implicitPoint = conicTool.pointAt(implicitParameter);
                const curvePoint = curveAdaptor.value(curveParameter);
                const tolerance = Math.max(
                    implicitParameter === implicitDomain.firstParameter
                        ? implicitDomain.firstTolerance
                        : implicitDomain.lastTolerance,
                    curveParameter === curveDomain.firstParameter
                        ? curveDomain.firstTolerance
                        : curveDomain.lastTolerance,
                );
                if (implicitPoint.distanceTo(curvePoint) > tolerance) return;
                const transitions = this.determineTransitions(
                    conicTool,
                    curveAdaptor,
                    implicitDomain,
                    curveDomain,
                    implicitParameter,
                    curveParameter,
                    implicitPoint,
                );
                this.insertPoint(points, {
                    point: implicitPoint,
                    implicitParameter,
                    curveParameter,
                    implicitTransition: transitions.implicitTransition,
                    curveTransition: transitions.curveTransition,
                });
            });
        });
    }

    /** 判断隐式参数是否位于 domain，越界时按端点世界距离容差兜底。 */
    private isParameterAccepted(
        parameter: number,
        curvePoint: THREE.Vector2,
        domain: ParametricIntersectionDomain2D,
    ): boolean {
        if (domain.firstParameter !== null && parameter < domain.firstParameter) {
            return domain.firstPoint !== null && curvePoint.distanceTo(domain.firstPoint) <= domain.firstTolerance;
        }
        if (domain.lastParameter !== null && parameter > domain.lastParameter) {
            return domain.lastPoint !== null && curvePoint.distanceTo(domain.lastPoint) <= domain.lastTolerance;
        }
        return true;
    }

    /** 按双方参数和完整 transition 插入并去重 point。 */
    private insertPoint(points: ImplicitParametricPointIntersection2D[], point: ImplicitParametricPointIntersection2D): void {
        const duplicate = points.some((candidate) => (
            Math.abs(candidate.implicitParameter - point.implicitParameter)
                < ImplicitParametricCurveIntersector2D.PARAMETER_EQUAL_TOLERANCE
            && Math.abs(candidate.curveParameter - point.curveParameter)
                < ImplicitParametricCurveIntersector2D.PARAMETER_EQUAL_TOLERANCE
            && this.transitionsEqual(candidate.implicitTransition, point.implicitTransition)
            && this.transitionsEqual(candidate.curveTransition, point.curveTransition)
        ));
        if (duplicate) return;
        points.push(point);
    }

    /** 合并双方参数连续且方向一致的 composite segment。 */
    private appendSegment(
        segments: ImplicitParametricSegmentIntersection2D[],
        segment: ImplicitParametricSegmentIntersection2D,
    ): void {
        const existing = segments.find((candidate) => (
            candidate.opposite === segment.opposite
            && this.segmentEndpointsEqual(candidate.secondEndpoint, segment.firstEndpoint)
        ));
        if (existing) {
            existing.secondEndpoint = segment.secondEndpoint;
            return;
        }
        segments.push(segment);
    }

    /**
     * 移除参数样条上已经被重合段覆盖的离散点。
     *
     * 圆和椭圆的隐式参数会在公开周期域内规范化，而重合段端点为了表达连续方向可能展开到
     * 周期域之外，因此不能再用隐式参数最小值和最大值构造矩形。样条参数始终连续且唯一，
     * 可直接作为重合范围的权威判据，并在端点处使用公共参数容差吸收求根误差。
     * @param points 待过滤的离散交点集合。
     * @param segments 已识别的连续重合段集合。
     */
    private removePointsInsideSegments(
        points: ImplicitParametricPointIntersection2D[],
        segments: readonly ImplicitParametricSegmentIntersection2D[],
    ): void {
        for (let index = points.length - 1; index >= 0; index -= 1) {
            const point = points[index]!;
            if (segments.some((segment) => {
                const curveMinimum = Math.min(
                    segment.firstEndpoint.curveParameter,
                    segment.secondEndpoint.curveParameter,
                );
                const curveMaximum = Math.max(
                    segment.firstEndpoint.curveParameter,
                    segment.secondEndpoint.curveParameter,
                );
                return point.curveParameter
                        >= curveMinimum - ImplicitParametricCurveIntersector2D.PARAMETER_EQUAL_TOLERANCE
                    && point.curveParameter
                        <= curveMaximum + ImplicitParametricCurveIntersector2D.PARAMETER_EQUAL_TOLERANCE;
            })) points.splice(index, 1);
        }
    }

    /** 创建 transition。 */
    private createTransition(
        position: ImplicitParametricIntersectionPosition2D,
        type: ImplicitParametricTransitionType2D,
        situation: ImplicitParametricIntersectionSituation2D,
        opposite: boolean,
    ): ImplicitParametricIntersectionTransition2D {
        return {position, type, situation, opposite};
    }

    /** 比较完整 transition。 */
    private transitionsEqual(first: ImplicitParametricIntersectionTransition2D, second: ImplicitParametricIntersectionTransition2D): boolean {
        return first.position === second.position
            && first.type === second.type
            && first.situation === second.situation
            && first.opposite === second.opposite;
    }

    /** 比较 segment 双方参数端点。 */
    private segmentEndpointsEqual(
        first: ImplicitParametricSegmentEndpoint2D,
        second: ImplicitParametricSegmentEndpoint2D,
    ): boolean {
        return Math.abs(first.implicitParameter - second.implicitParameter)
                < ImplicitParametricCurveIntersector2D.PARAMETER_EQUAL_TOLERANCE
            && Math.abs(first.curveParameter - second.curveParameter)
                < ImplicitParametricCurveIntersector2D.PARAMETER_EQUAL_TOLERANCE;
    }

    /** 计算二维标量叉积。 */
    private cross(first: THREE.Vector2, second: THREE.Vector2): number {
        return first.x * second.y - first.y * second.x;
    }
}
